import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { MapContainer, Marker, Polyline, Circle, Popup, useMap } from 'react-leaflet';
import { MapboxTileLayer, makeCircleIcon, L } from '../../lib/leafletInit';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { db } from '../../firebase';
import { fetchSupervisorLocationHistory } from '../../lib/supervisorLocationApi';

const INDIA_CENTER = [20.5937, 78.9629];

const pad2 = (n) => String(n).padStart(2, '0');
const fmtTime = (d) => d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}` : '—';
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${fmtTime(d)}`;
};
const deltaSec = (a, b) => {
  if (!a || !b) return null;
  const ta = a.toDate ? a.toDate().getTime() : 0;
  const tb = b.toDate ? b.toDate().getTime() : 0;
  return Math.round(Math.abs(tb - ta) / 1000);
};
const fmtSec = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};
const haversineMeters = (a, b) => {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat));
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

// ---------- map helpers ----------

/** Fit the map view to a set of lat/lng points. */
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 16);
      return;
    }
    const latlngs = points.map((p) => [p.lat, p.lng]);
    map.fitBounds(latlngs, { padding: [40, 40] });
  }, [map, points]);
  return null;
};

// ---------- UI helpers ----------

const mono = { fontFamily: 'var(--font-mono)' };

const Label = ({ children }) => (
  <Typography sx={{ ...mono, fontSize: 9, letterSpacing: '.16em', color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: '2px' }}>
    {children}
  </Typography>
);

const Value = ({ children }) => (
  <Typography sx={{ ...mono, fontSize: 13, fontVariantNumeric: 'tabular-nums slashed-zero', color: 'var(--c-ink)', lineHeight: 1.3, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
    {children}
  </Typography>
);

const Field = ({ label, value }) => (
  <Box sx={{ marginBottom: 'var(--sp-3)' }}>
    <Label>{label}</Label>
    <Value>{value ?? '—'}</Value>
  </Box>
);

const Badge = ({ label, color, bg }) => (
  <span style={{ ...mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 700, color, background: bg, padding: '3px 7px', marginRight: 4, display: 'inline-block' }}>
    {label}
  </span>
);

const Thumb = ({ url, label }) => {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ border: '1px solid var(--c-line-soft)', background: 'var(--c-bg-2)', padding: 0, cursor: 'pointer', width: 80, height: 80, overflow: 'hidden', borderRadius: 0 }}
        title={label}
      >
        <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img src={url} alt={label} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
        </div>
      )}
    </>
  );
};

// ---------- main ----------

const FeedbackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(true);
  const [route, setRoute] = useState([]);
  const [siteCircle, setSiteCircle] = useState(null);

  // Fetch the feedback record.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRecLoading(true);
      try {
        const snap = await getDoc(doc(db, 'feedback_collection', id));
        if (cancelled) return;
        setRec(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (err) {
        console.error('FeedbackDetail fetch error:', err);
        if (!cancelled) setRec(null);
      } finally {
        if (!cancelled) setRecLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Once we have the record, fetch supervisor route ±30 min and site circle.
  useEffect(() => {
    if (!rec) return;
    let cancelled = false;

    const supervisorUid = rec.feedback_collector_firebase_userID || rec.feedback_collector_supervisor_doc_id;
    const centerTs = rec.feedback_collection_DateTime;

    if (supervisorUid && centerTs) {
      const centerDate = centerTs.toDate ? centerTs.toDate() : new Date(centerTs);
      const windowStart = new Date(centerDate.getTime() - 30 * 60 * 1000);
      const windowEnd = new Date(centerDate.getTime() + 30 * 60 * 1000);
      (async () => {
        try {
          const result = await fetchSupervisorLocationHistory({
            feedbackSupervisorUid: supervisorUid,
            startMs: windowStart.getTime(),
            endMs: windowEnd.getTime(),
            limit: 200,
          });
          if (cancelled) return;
          const pts = (result.samples ?? []).map((s) => ({ lat: s.lat, lng: s.lng }));
          setRoute(pts);
        } catch (err) {
          console.error('FeedbackDetail route fetch error:', err);
        }
      })();
    }

    if (rec.resolvedSiteId) {
      (async () => {
        try {
          const ssnap = await getDoc(doc(db, 'sites', rec.resolvedSiteId));
          if (cancelled) return;
          if (ssnap.exists()) {
            const sd = ssnap.data();
            const lat = sd.lat ?? sd.latitude;
            const lng = sd.lng ?? sd.longitude;
            if (lat != null && lng != null) {
              setSiteCircle({ lat, lng, radiusM: sd.radiusM ?? sd.radius ?? 50 });
            }
          }
        } catch (err) {
          console.error('FeedbackDetail site fetch error:', err);
        }
      })();
    }

    return () => { cancelled = true; };
  }, [rec]);

  const geoToPoint = (geo) => {
    if (!geo) return null;
    const lat = geo.lat ?? geo.latitude;
    const lng = geo.lng ?? geo.longitude;
    if (lat == null || lng == null) return null;
    return { lat, lng };
  };

  const pinSubmit = useMemo(() => geoToPoint(rec?.geoAtSubmit), [rec]);
  const pinOTPReq = useMemo(() => geoToPoint(rec?.geoAtOTPRequest), [rec]);
  const pinOTPVer = useMemo(() => geoToPoint(rec?.geoAtOTPVerify), [rec]);

  const allPoints = useMemo(() => {
    const pts = [];
    if (pinSubmit) pts.push(pinSubmit);
    if (pinOTPReq) pts.push(pinOTPReq);
    if (pinOTPVer) pts.push(pinOTPVer);
    route.forEach((p) => pts.push(p));
    if (siteCircle) pts.push({ lat: siteCircle.lat, lng: siteCircle.lng });
    return pts;
  }, [pinSubmit, pinOTPReq, pinOTPVer, route, siteCircle]);

  const mapCenter = pinSubmit ?? allPoints[0] ?? { lat: INDIA_CENTER[0], lng: INDIA_CENTER[1] };

  const isSuspicious = rec?.flags?.includes?.('suspicious') || Number(rec?.displacementOTPtoSubmitM || 0) > 500;
  const isMocked = !!rec?.mockedAtAnyStage;

  const stars = Number(rec?.feedback_stars || 0);

  const dtReq = rec?.otpRequestedAt;
  const dtVer = rec?.otpVerifiedAt;
  const dtSub = rec?.feedback_collection_DateTime;
  const reqToVer = deltaSec(dtReq, dtVer);
  const verToSub = deltaSec(dtVer, dtSub);
  const reqToSub = deltaSec(dtReq, dtSub);

  const displacementM = rec?.displacementOTPtoSubmitM != null
    ? `${Math.round(rec.displacementOTPtoSubmitM)} m`
    : (pinOTPVer && pinSubmit ? `${Math.round(haversineMeters(pinOTPVer, pinSubmit))} m (calc)` : '—');

  if (recLoading) {
    return (
      <div style={{ padding: 'var(--sp-8)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)' }}>
        LOADING…
      </div>
    );
  }

  if (!rec) {
    return (
      <div style={{ padding: 'var(--sp-8)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-danger)' }}>
        RECORD NOT FOUND — ID: {id}
      </div>
    );
  }

  // Pre-build dashed OTP trail points
  const otpTrailPts = [pinOTPReq, pinOTPVer, pinSubmit].filter(Boolean);

  return (
    <div className="db-page" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div className="db-ribbon" style={{ borderBottom: '1px solid var(--c-line-soft)', padding: 'var(--sp-3) var(--pad-h)', minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <IconButton
            size="small"
            onClick={() => navigate('/feedback/admin')}
            aria-label="back to list"
            sx={{ color: 'var(--c-ink-2)', borderRadius: 0, border: '1px solid var(--c-line-soft)', '&:hover': { background: 'var(--c-bg-2)' } }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
            BACK TO LIST
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap', minWidth: 0 }}>
          {isSuspicious && <Badge label="🚩 SUSPICIOUS" color="#dc2626" bg="rgba(220,38,38,.10)" />}
          {isMocked && <Badge label="🎭 MOCKED" color="var(--c-ink-3)" bg="var(--c-bg-2)" />}
        </Box>
      </div>

      {/* Body: map left 2/3, rail right 1/3 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, height: 'calc(100vh - 56px)', minHeight: 'calc(100vh - 56px)' }}>
        {/* MAP */}
        <Box sx={{ position: 'relative', borderRight: { md: '1px solid var(--c-line-soft)' }, background: 'var(--c-bg-2)', minHeight: 360 }}>
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={15}
            style={{ width: '100%', height: '100%' }}
            zoomControl
          >
            <MapboxTileLayer />
            <FitBounds points={allPoints} />

            {/* Supervisor route polyline (amber) */}
            {route.length >= 2 && (
              <Polyline
                positions={route.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: '#EA580C', opacity: 0.85, weight: 3 }}
              />
            )}

            {/* Dashed OTP feedback trail — react-leaflet uses dashArray for dashes */}
            {otpTrailPts.length >= 2 && (
              <Polyline
                positions={otpTrailPts.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: '#94a3b8', opacity: 0.8, weight: 2, dashArray: '6 6' }}
              />
            )}

            {/* Site geofence circle */}
            {siteCircle && (
              <Circle
                center={[siteCircle.lat, siteCircle.lng]}
                radius={siteCircle.radiusM}
                pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.2, weight: 2, opacity: 0.8 }}
              />
            )}

            {/* Per-stage anomaly rings (20 m radius circles) */}
            {pinOTPReq && rec?.geoAtOTPRequest?.isMocked && (
              <Circle
                center={[pinOTPReq.lat, pinOTPReq.lng]}
                radius={20}
                pathOptions={{ color: '#DC2626', fillColor: '#DC2626', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}
            {pinOTPReq && !rec?.geoAtOTPRequest?.isMocked && rec?.geoAtOTPRequest?.accuracy > 100 && (
              <Circle
                center={[pinOTPReq.lat, pinOTPReq.lng]}
                radius={20}
                pathOptions={{ color: '#D97706', fillColor: '#D97706', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}
            {pinOTPVer && rec?.geoAtOTPVerify?.isMocked && (
              <Circle
                center={[pinOTPVer.lat, pinOTPVer.lng]}
                radius={20}
                pathOptions={{ color: '#DC2626', fillColor: '#DC2626', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}
            {pinOTPVer && !rec?.geoAtOTPVerify?.isMocked && rec?.geoAtOTPVerify?.accuracy > 100 && (
              <Circle
                center={[pinOTPVer.lat, pinOTPVer.lng]}
                radius={20}
                pathOptions={{ color: '#D97706', fillColor: '#D97706', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}
            {pinSubmit && rec?.geoAtSubmit?.isMocked && (
              <Circle
                center={[pinSubmit.lat, pinSubmit.lng]}
                radius={20}
                pathOptions={{ color: '#DC2626', fillColor: '#DC2626', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}
            {pinSubmit && !rec?.geoAtSubmit?.isMocked && rec?.geoAtSubmit?.accuracy > 100 && (
              <Circle
                center={[pinSubmit.lat, pinSubmit.lng]}
                radius={20}
                pathOptions={{ color: '#D97706', fillColor: '#D97706', fillOpacity: 0.12, weight: 2, opacity: 0.9 }}
              />
            )}

            {/* OTP Request pin — orange */}
            {pinOTPReq && (
              <Marker position={[pinOTPReq.lat, pinOTPReq.lng]} icon={makeCircleIcon('#f97316', 16)}>
                <Popup>OTP Request</Popup>
              </Marker>
            )}

            {/* OTP Verify pin — yellow */}
            {pinOTPVer && (
              <Marker position={[pinOTPVer.lat, pinOTPVer.lng]} icon={makeCircleIcon('#eab308', 16)}>
                <Popup>OTP Verify</Popup>
              </Marker>
            )}

            {/* Submit pin — red */}
            {pinSubmit && (
              <Marker position={[pinSubmit.lat, pinSubmit.lng]} icon={makeCircleIcon('#dc2626', 18)}>
                <Popup>Submission</Popup>
              </Marker>
            )}

            {/* Camera icon at submit location (selfie photo) */}
            {pinSubmit && rec?.feedback_selfiePhoto1 && (
              <Marker
                position={[pinSubmit.lat + 0.00005, pinSubmit.lng]}
                icon={L.divIcon({
                  className: '',
                  html: `<div title="Selfie photo" style="width:20px;height:20px;background:#1e293b;border-radius:3px;display:flex;align-items:center;justify-content:center;font-size:11px;cursor:pointer;">📷</div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
                eventHandlers={{ click: () => window.open(rec.feedback_selfiePhoto1, '_blank') }}
              >
                <Popup>Selfie photo</Popup>
              </Marker>
            )}

            {/* Displacement label at midpoint of OTP trail */}
            {pinOTPReq && pinSubmit && rec?.displacementOTPtoSubmitM != null && (
              <Marker
                position={[(pinOTPReq.lat + pinSubmit.lat) / 2, (pinOTPReq.lng + pinSubmit.lng) / 2]}
                icon={L.divIcon({
                  className: '',
                  html: `<div style="background:rgba(255,255,255,0.85);border:1px solid #e2e8f0;font-family:var(--font-mono,monospace);font-size:10px;letter-spacing:.08em;padding:2px 6px;color:#475569;white-space:nowrap;">Δ ${Math.round(rec.displacementOTPtoSubmitM)} m</div>`,
                  iconSize: null,
                  iconAnchor: [0, 0],
                })}
              >
                <Popup>Displacement: {Math.round(rec.displacementOTPtoSubmitM)} m</Popup>
              </Marker>
            )}
          </MapContainer>

          {/* Map legend */}
          <Box sx={{
            position: 'absolute', bottom: 12, left: 12, zIndex: 1000,
            background: 'var(--c-bg)', border: '1px solid var(--c-line-soft)',
            padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '4px',
            maxWidth: 'calc(100% - 24px)',
          }}>
            {[
              { color: '#dc2626', label: 'SUBMISSION', shape: 'circle' },
              { color: '#f97316', label: 'OTP REQUEST', shape: 'circle' },
              { color: '#eab308', label: 'OTP VERIFY', shape: 'circle' },
              { color: '#EA580C', label: 'ROUTE ±30 MIN', shape: 'line' },
              { color: '#94a3b8', label: 'FEEDBACK TRAIL', shape: 'dashed' },
              { color: '#2563eb', label: 'SITE GEOFENCE', shape: 'circle' },
              { color: '#DC2626', label: 'MOCKED STAGE', shape: 'ring' },
              { color: '#D97706', label: 'LOW ACCURACY', shape: 'ring' },
            ].map(({ color, label, shape }) => (
              <span key={label} style={{ ...mono, fontSize: 9, letterSpacing: '.10em', color: 'var(--c-ink-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {shape === 'line' && (
                  <span style={{ display: 'inline-block', width: 14, height: 2, background: color, flexShrink: 0 }} />
                )}
                {shape === 'dashed' && (
                  <span style={{ display: 'inline-block', width: 14, height: 2, flexShrink: 0, backgroundImage: `repeating-linear-gradient(to right, ${color} 0, ${color} 4px, transparent 4px, transparent 7px)` }} />
                )}
                {shape === 'circle' && (
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                )}
                {shape === 'ring' && (
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'transparent', border: `2px solid ${color}`, flexShrink: 0 }} />
                )}
                {label}
              </span>
            ))}
          </Box>
        </Box>

        {/* RIGHT RAIL */}
        <Box sx={{ overflowY: 'auto', padding: 'var(--sp-4)', background: 'var(--c-bg)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
          {/* Shop */}
          <Box>
            <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
              SHOP
            </Typography>
            <Typography sx={{ fontFamily: "'Geist', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--c-ink)', marginBottom: 'var(--sp-1)' }}>
              {rec.shop_name || '—'}
            </Typography>
            <Typography sx={{ ...mono, fontSize: 11, color: '#EA580C', letterSpacing: '.06em', marginBottom: 'var(--sp-3)' }}>
              {'★'.repeat(stars)}{'☆'.repeat(Math.max(0, 5 - stars))} {stars}/5
            </Typography>
            <Field label="OWNER" value={rec.shop_owner_name} />
            <Field label="ADDRESS" value={[rec.shop_address, rec.shop_area, rec.shop_pincode].filter(Boolean).join(', ')} />
            <Field label="PHONE" value={rec.phone_number} />
          </Box>

          {/* Feedback */}
          {rec.feedback_Message && (
            <Box sx={{ border: '1px solid var(--c-line-soft)', padding: 'var(--sp-3)' }}>
              <Label>FEEDBACK TEXT</Label>
              <Typography sx={{ fontFamily: "'Geist', sans-serif", fontSize: 13, color: 'var(--c-ink)', lineHeight: 1.5, marginTop: 'var(--sp-1)' }}>
                {rec.feedback_Message}
              </Typography>
            </Box>
          )}

          {/* Photos */}
          {(rec.feedback_selfiePhoto1 || rec.feedback_areaPhoto2 || rec.feedback_areaPhoto3) && (
            <Box>
              <Label>PHOTOS</Label>
              <Box sx={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 'var(--sp-2)' }}>
                <Thumb url={rec.feedback_selfiePhoto1} label="Selfie" />
                <Thumb url={rec.feedback_areaPhoto2} label="Area 1" />
                <Thumb url={rec.feedback_areaPhoto3} label="Area 2" />
              </Box>
            </Box>
          )}

          {/* Collector */}
          <Box>
            <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
              COLLECTOR
            </Typography>
            <Field label="NAME" value={rec.feedback_collector_name} />
            <Field label="IXORA ID" value={rec.feedback_collector_ixora_user_id} />
            <Field label="SUBMITTED" value={fmtDateTime(dtSub)} />
          </Box>

          {/* Timing */}
          <Box>
            <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
              TIMING
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--sp-2)' }}>
              {[
                { label: 'OTP REQUEST', value: fmtDateTime(dtReq) },
                { label: 'OTP VERIFY', value: fmtDateTime(dtVer) },
                { label: 'REQ → VER', value: fmtSec(reqToVer) },
                { label: 'VER → SUB', value: fmtSec(verToSub) },
                { label: 'TOTAL DWELL', value: fmtSec(reqToSub) },
                { label: 'DISPLACEMENT', value: displacementM },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ border: '1px solid var(--c-line-soft)', padding: 'var(--sp-2) var(--sp-3)' }}>
                  <Label>{label}</Label>
                  <Value>{value}</Value>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Geofence */}
          <Box>
            <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
              GEOFENCE
            </Typography>
            <Field label="RESOLVED SITE" value={rec.resolvedSiteId ?? 'NONE'} />
            <Field label="DISTANCE" value={rec.resolvedSiteDistanceM != null ? `${Math.round(rec.resolvedSiteDistanceM)} m` : '—'} />
            <Field label="MATCH KIND" value={rec.geofenceMatchKind?.toUpperCase() ?? '—'} />
          </Box>

          {/* Device */}
          {rec.deviceInfo && (
            <Box>
              <Typography sx={{ ...mono, fontSize: 10, letterSpacing: '.16em', fontWeight: 600, color: 'var(--c-ink-3)', textTransform: 'uppercase', marginBottom: 'var(--sp-3)' }}>
                DEVICE
              </Typography>
              <Field label="MODEL" value={[rec.deviceInfo.manufacturer, rec.deviceInfo.model].filter(Boolean).join(' ')} />
              <Field label="OS" value={[rec.deviceInfo.osName, rec.deviceInfo.osVersion].filter(Boolean).join(' ')} />
              <Field label="BUILD" value={rec.deviceInfo.appBuildNumber} />
              <Field label="NETWORK" value={rec.networkTypeAtSubmit?.toUpperCase()} />
            </Box>
          )}

          {/* Flags */}
          {(isSuspicious || isMocked || rec.silent_duplicate || rec.flags?.length || rec.installationId) && (
            <Box>
              <Label>FLAGS</Label>
              <Box sx={{ marginTop: 'var(--sp-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}>
                {rec.silent_duplicate === true && <Badge label="👻 SILENT DUPLICATE" color="#7c3aed" bg="rgba(124,58,237,.10)" />}
                {isSuspicious && <Badge label="🚩 SUSPICIOUS" color="#dc2626" bg="rgba(220,38,38,.10)" />}
                {isMocked && <Badge label="🎭 MOCKED" color="var(--c-ink-3)" bg="var(--c-bg-2)" />}
                {rec.flags?.filter((f) => f !== 'suspicious' && f !== 'silent_duplicate').map((f) => {
                  const isHigh = ['impossible_travel', 'mocked_location', 'image_duplicate'].includes(f);
                  return (
                    <Badge
                      key={f}
                      label={f.toUpperCase().replace(/_/g, ' ')}
                      color={isHigh ? '#dc2626' : '#d97706'}
                      bg={isHigh ? 'rgba(220,38,38,.10)' : 'rgba(217,119,6,.10)'}
                    />
                  );
                })}
              </Box>
              {rec.installationId && (
                <Box sx={{ marginTop: 'var(--sp-3)' }}>
                  <Label>DEVICE FINGERPRINT</Label>
                  <Box sx={{ marginTop: 'var(--sp-1)', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--c-ink-2)', wordBreak: 'break-all' }}>
                    {rec.installationId}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default FeedbackDetail;
